import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Smartphone, Download, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useLanguage } from '../LanguageProvider';

type Lang = 'tr' | 'en';
type Localized<T> = Record<Lang, T>;

export function MobileAppPage() {
  const { productSlug } = useParams();
  const { language } = useLanguage();
  const routeSlug = productSlug ?? '';
  /** SHALTERON S aynı uygulamayı kullanır; veri anahtarı shalteron. */
  const appDataKey = routeSlug === 'shalteron-s' ? 'shalteron' : routeSlug;
  const getLocalized = <T,>(value: Localized<T>): T => value[language] ?? value.tr;

  const texts = {
    tr: {
      products: 'Ürünler',
      mobileApp: 'Mobil Uygulama',
      appNotFound: 'Uygulama Bulunamadı',
      appNotFoundDesc: 'Aradığınız mobil uygulama mevcut değil.',
      backToProducts: 'Ürünlere Dön',
      downloadFromPlayStore: 'Play Store\'dan İndir',
      downloadFromAppStore: 'App Store\'dan İndir',
      appUsageVideo: 'Uygulama Kullanım Videosu',
      appFeatures: 'Uygulama Özellikleri',
      systemRequirements: 'Sistem Gereksinimleri',
      osAndroid: 'İşletim Sistemi (Android)',
      osIOS: 'İşletim Sistemi (iOS)',
      connection: 'Bağlantı',
      deviceStatus: 'Cihaz Durumu',
      downloadNow: 'Uygulamayı Hemen İndirin',
      downloadDesc: 'cihazınızı mobil uygulamamız ile kolayca yönetin. Hemen indirin ve deneyim farkını yaşayın.',
      backToProductDetails: 'Ürün Detaylarına Dön'
    },
    en: {
      products: 'Products',
      mobileApp: 'Mobile App',
      appNotFound: 'App Not Found',
      appNotFoundDesc: 'The mobile app you are looking for does not exist.',
      backToProducts: 'Back to Products',
      downloadFromPlayStore: 'Download from Play Store',
      downloadFromAppStore: 'Download from App Store',
      appUsageVideo: 'App Usage Video',
      appFeatures: 'App Features',
      systemRequirements: 'System Requirements',
      osAndroid: 'Operating System (Android)',
      osIOS: 'Operating System (iOS)',
      connection: 'Connection',
      deviceStatus: 'Device Status',
      downloadNow: 'Download the App Now',
      downloadDesc: 'Easily manage your device with our mobile app. Download now and experience the difference.',
      backToProductDetails: 'Back to Product Details'
    }
  } as const;

  const t = texts[language] ?? texts.tr;

  const mobileApps: Record<string, {
    title: Localized<string>;
    productTitle: Localized<string>;
    description: Localized<string>;
    icon: string;
    iconPath: string;
    heroImagePath: string;
    downloadUrl: string;
    appStoreUrl?: string;
    features: Localized<string[]>;
    screenshots: string[];
    systemRequirements: Localized<string[]>;
    rating: number;
    downloads: string;
    category: Localized<string>;
  }> = {
    'shalteron': {
      title: { tr: 'ShalterOn', en: 'ShalterOn' },
      productTitle: { tr: 'ShalterOn', en: 'ShalterOn' },
      description: {
        tr: 'ShalterOn mobil uygulaması ile sulama sistemleri, fanlar, havalandırma, ısıtma, aydınlatma, jeneratörler, motorlar ve güvenlik sistemlerini uzaktan açıp kapatabilir, çalışma durumlarını anlık olarak izleyebilirsiniz. Sera ve tarımsal otomasyondan endüstriyel tesislere kadar sahadaki sistemleri sensör gerektirmeden hızlı ve ekonomik şekilde uzaktan yönetmenin en pratik yolunu sunar.',
        en: 'With ShalterOn mobile app, you can remotely turn on/off irrigation systems, fans, ventilation, heating, lighting, generators, motors and security systems. You can also monitor their operating status instantly. It provides the most practical way to remotely manage systems without requiring sensors, quickly and economically, from greenhouses and agricultural automation to industrial facilities.'
      },
      icon: '📱',
      iconPath: '/akilli-cihaz-yonetimi-ikon.png',
      heroImagePath: '/shalteron-uygulama.png',
      downloadUrl: 'https://play.google.com/store/apps/details?id=com.ilimera.akilli_cihaz_yonetimi&pcampaignid=web_share',
      appStoreUrl: 'https://apps.apple.com/tr/app/shalteron/id6755028818?l=tr',
      features: {
        tr: [
          'Uzaktan cihaz kontrolü',
          'Zamanlama ve programlama',
          'Gerçek zamanlı durum takibi',
          'SMS bildirimleri',
          'Güvenli bağlantı',
          'Kullanıcı dostu arayüz'
        ],
        en: [
          'Remote device control',
          'Scheduling and programming',
          'Real-time status tracking',
          'SMS notifications',
          'Secure connection',
          'User-friendly interface'
        ]
      },
      screenshots: [
        '/app-screenshot-1.png',
        '/app-screenshot-2.png',
        '/app-screenshot-3.png'
      ],
      systemRequirements: {
        tr: [
          'Android 6.0 veya üzeri',
          'iOS 14.0 veya üzeri',
          'İnternet bağlantısı'

        ],
        en: [
          'Android 6.0 or higher',
          'iOS 14.0 or higher',
          'Internet connection'
        ]
      },
      rating: 4.8,
      downloads: '10K+',
      category: { tr: 'Otomasyon & Kontrol', en: 'Automation & Control' }
    },
    'frigolive': {
      title: { tr: 'Frigolive', en: 'Frigolive' },
      productTitle: { tr: 'Frigolive', en: 'Frigolive' },
      description: {
        tr: 'Frigolive sıcaklık takip cihazınızı mobil uygulamamız ile kolayca yönetin. Gerçek zamanlı sıcaklık verilerini takip edin, alarm ayarları yapın ve geçmiş verileri analiz edin.',
        en: 'Easily manage your Frigolive temperature tracking device with our mobile app. Track real-time temperature data, set alarms and analyze historical data.'
      },
      icon: '🌡️',
      iconPath: '/frigolive-uygulama-icon.png',
      heroImagePath: '/frigolive-login.png',
      downloadUrl: 'https://play.google.com/store/apps/details?id=com.ilimera.frigolivellogger&pcampaignid=web_share',
      appStoreUrl: 'https://apps.apple.com/tr/app/frigolive/id6758705163?l=tr',
      features: {
        tr: [
          'Gerçek zamanlı sıcaklık takibi',
          'Alarm ve bildirim sistemi',
          'Geçmiş veri analizi',
          'Grafik ve raporlama',
          'Çoklu cihaz desteği',
          'Offline veri senkronizasyonu'
        ],
        en: [
          'Real-time temperature tracking',
          'Alarm and notification system',
          'Historical data analysis',
          'Graphics and reporting',
          'Multiple device support',
          'Offline data synchronization'
        ]
      },
      screenshots: [
        '/app-screenshot-1.png',
        '/app-screenshot-2.png',
        '/app-screenshot-3.png'
      ],
      systemRequirements: {
        tr: [
          'Android 7.0 veya üzeri',
          'iOS 14.0 veya üzeri',
          'İnternet bağlantısı',
          'Frigolive cihazı aktif olmalı'
        ],
        en: [
          'Android 7.0 or higher',
          'iOS 14.0 or higher',
          'Internet connection',
          'Frigolive device must be active'
        ]
      },
      rating: 4.7,
      downloads: '5K+',
      category: { tr: 'IoT & Sensörler', en: 'IoT & Sensors' }
    },
    'akilli-gecis-sistemi': {
      title: { tr: 'Akıllı Geçiş Sistemi', en: 'Smart Access System' },
      productTitle: { tr: 'Akıllı Geçiş Sistemi', en: 'Smart Access System' },
      description: {
        tr:
          'İlimera AGS mobil uygulaması, geçişleri hızlandıran ve temassız kılan mobil odaklı bir deneyim sunar: size tanımlanan dinamik QR kodu okutarak birçok senaryoda fiziksel karta gerek kalmadan kapı ve turnikelerden geçebilirsiniz; kartlı geçiş aynı altyapıda çalışmaya devam eder ve kurumlar kullanıcı profiline göre yalnızca kart, yalnızca mobil veya hibrit kullanım tanımlayabilir. Süre veya kullanım sayısıyla sınırlı kodlarla misafir ve geçici erişimleri kontrollü yönetebilir, yoğun geçiş noktalarında kısa doğrulama ve net geri bildirimle bekleme ve hata riskini azaltabilirsiniz. Arayüz, farklı kullanıcı gruplarının minimum öğrenmeyle sistemi benimsemesi için sade ve anlaşılır tutulmuştur.',
        en:
          'The İlimera AGS mobile app offers a mobile-first, contactless experience for faster passage: scan your assigned dynamic QR at readers to enter without a plastic card in many scenarios, while card-based access continues on the same platform. You can define card-only, mobile-only or hybrid rules per user profile, issue time- or usage-limited codes for visitors and guests, and keep busy entrances moving with quick verification and clear feedback. The experience stays simple so diverse user groups can adopt it with little training.'
      },
      icon: '🔐',
      iconPath: '/ilimera_ags_402x402.png',
      heroImagePath: '/akilli-gecis-sistemi-uygulama.png',
      downloadUrl: 'https://play.google.com/store/apps/details?id=com.ilimera.kartli_gecis_sistemi&pcampaignid=web_share',
      features: {
        tr: [
          'QR kod ve RFID kart okuyucu',
          'Gerçek zamanlı giriş takibi',
          'Detaylı raporlama sistemi',
          'Yoklama ve vardiya yönetimi',
          'Zaman dilimi kontrolü',
          'Çoklu kullanıcı desteği'
        ],
        en: [
          'QR code and RFID card reader',
          'Real-time entry tracking',
          'Detailed reporting system',
          'Attendance and shift management',
          'Time zone control',
          'Multi-user support'
        ]
      },
      screenshots: [
        '/app-screenshot-1.png',
        '/app-screenshot-2.png',
        '/app-screenshot-3.png'
      ],
      systemRequirements: {
        tr: [
          'Android 6.0 veya üzeri',
          'İnternet bağlantısı',
          'Akıllı Geçiş cihazı aktif olmalı'
        ],
        en: [
          'Android 6.0 or higher',
          'Internet connection',
          'Smart Access device must be active'
        ]
      },
      rating: 4.6,
      downloads: '8K+',
      category: { tr: 'Güvenlik Sistemleri', en: 'Security Systems' }
    }
  };

  const app = mobileApps[appDataKey as keyof typeof mobileApps];

  if (!app) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{t.appNotFound}</h1>
        <p className="text-gray-600 mb-8">{t.appNotFoundDesc}</p>
        <Link to="/urunler">
          <Button>
            <ArrowLeft className="mr-2" size={16} />
            {t.backToProducts}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="py-12">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Link to="/urunler" className="hover:text-primary transition-colors">{t.products}</Link>
          <span>/</span>
          <Link to={`/urunler/${routeSlug}`} className="hover:text-primary transition-colors">
            {getLocalized(app.productTitle)}
          </Link>
          <span>/</span>
          <span className="text-foreground">{t.mobileApp}</span>
        </nav>
      </div>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center mb-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mr-4 text-2xl overflow-hidden">
                {(app as any).iconPath ? (
                  <img src={(app as any).iconPath} alt={`${getLocalized(app.title)} icon`} className="w-12 h-12 object-contain" />
                ) : (
                  <span>{app.icon}</span>
                )}
              </div>
              <div>
                <Badge className="mb-2 bg-muted text-green-800">
                  {getLocalized(app.category)}
                </Badge>
                <h1 className="text-3xl font-bold text-foreground">{getLocalized(app.title)}</h1>
              </div>
            </div>

            <p
              className="text-lg text-muted-foreground leading-relaxed mb-6"
              {...(appDataKey === 'shalteron' || routeSlug === 'akilli-gecis-sistemi'
                ? {
                    lang: language === 'en' ? 'en' : 'tr',
                    style: {
                      textAlign: 'justify' as const,
                      textJustify: 'inter-word' as const,
                      hyphens: 'auto' as const
                    }
                  }
                : {})}
            >
              {getLocalized(app.description)}
            </p>

            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90"
                onClick={() => window.open(app.downloadUrl, '_blank', 'noopener,noreferrer')}
              >
                <svg className="w-5 h-5 fill-white mr-2" viewBox="0 0 24 24">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                {t.downloadFromPlayStore}
              </Button>

              {(app as any).appStoreUrl && (
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90"
                  onClick={() => window.open((app as any).appStoreUrl, '_blank', 'noopener,noreferrer')}
                >
                  <svg className="w-5 h-5 fill-white mr-2" viewBox="0 0 24 24">
                    <path d="M16.365 1.43c0 1.14-.464 2.182-1.234 2.952-.787.787-1.826 1.267-2.904 1.186-.113-1.064.33-2.114 1.102-2.903.77-.788 1.957-1.358 3.036-1.235zM20.235 13.53c-.064-.13-2.45-1.327-2.55-4.02-.04-1.076.296-2.152 1.04-3.007-.513-.73-1.275-1.288-2.164-1.57-.233-.07-1.532-.293-2.892.616-.507.334-1.137.334-1.68 0-1.36-.91-2.66-.687-2.892-.615-.89.282-1.65.84-2.165 1.57.745.855 1.08 1.93 1.04 3.008-.1 2.692-2.484 3.89-2.547 4.018-.064.13-.607 2.195.46 4.316.403.79.91 1.488 1.51 2.08 1.062 1.05 2.26 1.05 2.75 1.05.255 0 .613-.08 1.02-.23.408-.15.88-.23 1.34-.23.45 0 .93.08 1.34.23.41.15.77.23 1.03.23.49 0 1.69 0 2.75-1.05.6-.59 1.11-1.29 1.51-2.08 1.07-2.12.52-4.18.46-4.317z" />
                  </svg>
                  {t.downloadFromAppStore}
                </Button>
              )}
            </div>
          </div>

          <div className="flex justify-center">
            <div className="w-64 h-98 bg-gray-100 dark:bg-gray-800 rounded-3xl flex items-center justify-center shadow-2xl overflow-hidden">
              {(app as any).heroImagePath ? (
                <img src={(app as any).heroImagePath} alt={`${getLocalized(app.title)} screenshot`} className="w-full h-full object-cover" />
              ) : (
                <Smartphone className="w-32 h-32 text-muted-foreground" />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* App Usage Video (Shalteron) */}
      {appDataKey === 'shalteron' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-2xl font-bold text-foreground mb-6">{t.appUsageVideo}</h2>
          <div className="max-w-2xl">
            <Card className="overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
              <CardContent className="p-0">
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    src="https://www.youtube.com/embed/jGjOazU2hR8"
                    title={t.appUsageVideo}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <h2 className="text-2xl font-bold text-foreground mb-8">{t.appFeatures}</h2>
        <ul className="max-w-3xl space-y-2 list-none pl-0 text-muted-foreground">
          {getLocalized(app.features).map((feature, index) => (
            <li key={index} className="flex items-center gap-2">
              <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M11.5 3.5L5.5 9.5L2.5 6.5" stroke="#348d5c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            {t.downloadNow}
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {getLocalized(app.productTitle)} {t.downloadDesc}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
            <Button
              size="lg"
              className="bg-primary hover:bg-primary/90"
              onClick={() => window.open(app.downloadUrl, '_blank', 'noopener,noreferrer')}
            >
              <svg className="w-5 h-5 fill-white mr-2" viewBox="0 0 24 24">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
              </svg>
              {t.downloadFromPlayStore}
            </Button>
            {(app as any).appStoreUrl && (
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90"
                onClick={() => window.open((app as any).appStoreUrl, '_blank', 'noopener,noreferrer')}
              >
                <svg className="w-5 h-5 fill-white mr-2" viewBox="0 0 24 24">
                  <path d="M16.365 1.43c0 1.14-.464 2.182-1.234 2.952-.787.787-1.826 1.267-2.904 1.186-.113-1.064.33-2.114 1.102-2.903.77-.788 1.957-1.358 3.036-1.235zM20.235 13.53c-.064-.13-2.45-1.327-2.55-4.02-.04-1.076.296-2.152 1.04-3.007-.513-.73-1.275-1.288-2.164-1.57-.233-.07-1.532-.293-2.892.616-.507.334-1.137.334-1.68 0-1.36-.91-2.66-.687-2.892-.615-.89.282-1.65.84-2.165 1.57.745.855 1.08 1.93 1.04 3.008-.1 2.692-2.484 3.89-2.547 4.018-.064.13-.607 2.195.46 4.316.403.79.91 1.488 1.51 2.08 1.062 1.05 2.26 1.05 2.75 1.05.255 0 .613-.08 1.02-.23.408-.15.88-.23 1.34-.23.45 0 .93.08 1.34.23.41.15.77.23 1.03.23.49 0 1.69 0 2.75-1.05.6-.59 1.11-1.29 1.51-2.08 1.07-2.12.52-4.18.46-4.317z" />
                </svg>
                {t.downloadFromAppStore}
              </Button>
            )}
            <Link to={`/urunler/${routeSlug}`}>
              <Button className="dark:bg-background" size="lg" variant="outline">
                {t.backToProductDetails}
                <ArrowLeft className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
